import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Checkbox,
  Toolbar,
  Chip,
  Menu,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  TableSortLabel,
  Tooltip,
  Divider,
  Grid,
  Card,
  CardContent,
  Badge,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  ButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  MoreVert as MoreIcon,
  Store as StoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Analytics as AnalyticsIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  ShoppingCart as OrderIcon,
  AttachMoney as MoneyIcon,
  Assessment as ReportIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  // PayPal icon not available, using Payment instead
  ArrowBack as ArrowBackIcon,
  PersonAdd as NewVendorIcon,
  Inventory as ProductsIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import api from '../services/api';
import { Vendor, ApiResponse } from '../types/api';

interface VendorFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  description?: string;
  status: 'active' | 'inactive';
}

interface EnhancedVendor extends Vendor {
  store_name?: string;
  enabled?: boolean;
  website?: string;
  description?: string;
  totalOrders?: number;
  totalRevenue?: number;
  monthlyRevenue?: { month: string; revenue: number; orders: number }[];
  paymentMethods?: PaymentMethod[];
  products?: VendorProduct[];
  recentOrders?: VendorOrder[];
}

interface PaymentMethod {
  id: number;
  type: 'bank_account';
  accountName: string;
  accountNumber: string;
  bankName: string;
  accountType: 'checking' | 'savings' | 'business';
  iban?: string;
  swiftCode?: string;
  routingNumber?: string;
  isDefault: boolean;
  isActive: boolean;
}

interface VendorProduct {
  id: number;
  name: string;
  price: number;
  stock: number;
  sales: number;
  revenue: number;
  category?: string;
  categories?: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}

interface VendorOrder {
  id: number;
  date: string;
  customer: string;
  amount: number;
  status: string;
  items: number;
}

type SortField = 'name' | 'email' | 'phone' | 'status' | 'created_at';
type SortDirection = 'asc' | 'desc';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vendor-tabpanel-${index}`}
      aria-labelledby={`vendor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const Vendors: React.FC = () => {
  // State Management
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedVendorProfile, setSelectedVendorProfile] = useState<EnhancedVendor | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<EnhancedVendor | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    description: '',
    status: 'active',
  });

  const queryClient = useQueryClient();

  // Enhanced function to process real vendor data and add missing analytics
  const enhanceVendorData = (vendors: EnhancedVendor[]) => {
    console.log('🔧 Enhancing vendor data...');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return vendors.map(vendor => {
      const productCount = vendor.products?.length || 0;
      console.log(`🔧 Enhancing vendor ${vendor.name}: ${productCount} products`);
      
      return {
        ...vendor,
      // Use real product data but add category field if missing
      products: (vendor.products || []).map((product: any, index: number) => {
        // Extract category from WooCommerce categories array
        let extractedCategory = 'General';
        if (product.categories && Array.isArray(product.categories) && product.categories.length > 0) {
          extractedCategory = product.categories[0].name || 'General';
          // Only log first product to check extraction
          if (index === 0) {
            console.log(`🔍 Sample product "${product.name}" → category: "${extractedCategory}"`);
          }
        }
        
        return {
          ...product,
          category: extractedCategory
        };
      }),
      // Generate monthly revenue only if not provided by API
      monthlyRevenue: vendor.monthlyRevenue || months.map(month => ({
        month,
        revenue: Math.floor(Math.random() * 5000) + 1000,
        orders: Math.floor(Math.random() * 50) + 10,
      })),
      // Generate payment methods only if not provided by API
      paymentMethods: vendor.paymentMethods || [
        {
          id: 1,
          type: 'bank_account' as const,
          accountName: `${vendor.name} Business Account`,
          accountNumber: `****${Math.floor(Math.random() * 9000) + 1000}`,
          bankName: ['Bank of Morocco', 'Attijariwafa Bank', 'BMCE Bank', 'Société Générale'][Math.floor(Math.random() * 4)],
          accountType: 'business' as const,
          iban: `MA${Math.floor(Math.random() * 90000000000000000000) + 10000000000000000000}`,
          swiftCode: ['BMCEMAMC', 'ATIJMAM1', 'BMCEMAM2'][Math.floor(Math.random() * 3)],
          isDefault: true,
          isActive: true,
        },
             ],
       // Generate recent orders only if not provided by API
       recentOrders: vendor.recentOrders || [],
      };
    });
  };

  // Fetch vendors data using direct fetch to bypass potential API service issues
  const { data: vendorsResponse, isLoading, error } = useQuery({
    queryKey: ['vendors-all-with-products'],
    queryFn: async () => {
      console.log('🔄 Making direct fetch call to backend for vendors with product data');
      
      // First fetch basic vendors list
      const response = await fetch('https://api.erp.oroud.net/api/vendors.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      console.log('✅ Basic vendors response:', {
        total: data.total,
        vendorsLength: data.vendors?.length,
        status: data.status,
        debug: data.debug
      });

            // For analytics, fetch detailed data for ALL vendors (or at least top performers + latest)
      if (data.vendors && data.vendors.length > 0) {
        // Get vendors for different analytics sections
        const sortedByDate = [...data.vendors].sort((a, b) => 
          new Date(b.created_at || b.updated_at || new Date()).getTime() - 
          new Date(a.created_at || a.updated_at || new Date()).getTime()
        );
        
        const sortedByRevenue = [...data.vendors].sort((a, b) => 
          (b.total_revenue || 0) - (a.total_revenue || 0)
        );
        
        // Get vendors for latest section (10 most recent)
        const latestVendors = sortedByDate.slice(0, 10);
        
        // Get vendors for analytics (top 10 performers)
        const topVendors = sortedByRevenue.slice(0, 10);
        
        // Combine and deduplicate vendors that need product data
        const vendorsNeedingProducts = new Map();
        [...latestVendors, ...topVendors].forEach(vendor => {
          vendorsNeedingProducts.set(vendor.id, vendor);
        });
        
        const vendorsToEnhance = Array.from(vendorsNeedingProducts.values());
        console.log(`🔄 Fetching product data for ${vendorsToEnhance.length} vendors (latest + top performers)`);
         
         // Fetch detailed data for all vendors that will appear in analytics
         const detailedVendors = await Promise.all(
           vendorsToEnhance.map(async (vendor: any) => {
            try {
              const detailResponse = await fetch(`https://api.erp.oroud.net/api/vendors.php?vendor_id=${vendor.id}&action=products`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              const productData = await detailResponse.json();
              
              console.log(`📦 Vendor ${vendor.id} (${vendor.store_name || vendor.name}) fetched ${productData.data?.length || 0} products`);
              
              return {
                ...vendor,
                products: productData.data || []
              };
            } catch (error) {
              console.error(`Error fetching products for vendor ${vendor.id}:`, error);
              return {
                ...vendor,
                products: []
              };
            }
          })
        );

        // Replace vendors in the main list with detailed ones
        const vendorMap = new Map(detailedVendors.map((v: any) => [v.id, v]));
        data.vendors = data.vendors.map((vendor: any) => 
          vendorMap.get(vendor.id) || vendor
        );

        console.log('✅ Enhanced vendors with product data for analytics and latest sections');
      }
      
      return data;
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes cache for this heavier query
  });

  // Fetch dashboard data for merchant performance analytics
  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard-vendor-analytics'],
    queryFn: () => api.getDashboard(),
    staleTime: 3 * 60 * 1000, // 3 minutes
  });

  // Parse vendors data with fallback handling
  const vendors: EnhancedVendor[] = useMemo(() => {
    console.log('🚀 Processing vendors data...');
    
    // Debug logging
    const response = vendorsResponse as any;
    const rawVendors = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.vendors)
      ? response.vendors
      : Array.isArray(response)
        ? response
        : [];

    console.log(`📊 Loaded ${rawVendors.length} vendors from API`);

    const enhancedVendors = rawVendors.map((vendor: any) => ({
      id: vendor.id,
      name: vendor.store_name || vendor.shop_name || vendor.name || 'Unnamed Vendor',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: typeof vendor.address === 'object' 
        ? [vendor.address?.street_1, vendor.address?.city, vendor.address?.country]
            .filter(Boolean).join(', ')
        : vendor.address || '',
      status: vendor.enabled ? 'active' : 'inactive',
      created_at: vendor.created_at || new Date().toISOString(),
      updated_at: vendor.updated_at || new Date().toISOString(),
      store_name: vendor.store_name || vendor.shop_name,
      enabled: vendor.enabled,
      website: vendor.website || '',
      description: vendor.description || '',
      totalOrders: vendor.total_orders || Math.floor(Math.random() * 100),
      totalRevenue: vendor.total_revenue || Math.floor(Math.random() * 10000),
    }));

    const finalVendors = enhanceVendorData(enhancedVendors);
    
    console.log(`📊 Processed ${finalVendors.length} vendors with enhanced data`);
    
    return finalVendors;
  }, [vendorsResponse]);

  // Analytics calculations
  const analyticsData = useMemo(() => {
    console.log('📊 Calculating analytics data...');
    console.log('📊 Total vendors for analytics:', vendors.length);
    
    const totalRevenue = vendors.reduce((sum, v) => sum + (v.totalRevenue || 0), 0);
    const totalOrders = vendors.reduce((sum, v) => sum + (v.totalOrders || 0), 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    
    // Calculate vendor growth metrics
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    // New vendors this week
    const newVendorsThisWeek = vendors.filter(vendor => {
      const createdDate = new Date(vendor.created_at || vendor.updated_at || now);
      return createdDate >= weekAgo;
    }).length;

    // Average products per vendor
    const avgProductsPerVendor = vendors.length > 0 
      ? vendors.reduce((sum, v) => sum + (v.products?.length || Math.floor(Math.random() * 25) + 1), 0) / vendors.length 
      : 0;

    // Vendor status distribution
    const vendorStatusCounts = vendors.reduce((acc, vendor) => {
      const status = vendor.status || 'active';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Monthly vendor growth trends (last 6 months)
    const vendorGrowthTrends = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      const monthName = date.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      
      // Simulate vendor growth data (in real app, this would come from actual data)
      const newVendors = Math.floor(Math.random() * 15) + 5; // 5-20 new vendors per month
      const totalVendorsUpToDate = Math.floor(vendors.length * (0.7 + (i * 0.05))); // Progressive growth
      
      return {
        month: monthName,
        newVendors,
        totalVendors: totalVendorsUpToDate,
        activeVendors: Math.floor(totalVendorsUpToDate * 0.85), // ~85% active
      };
    });

    // Latest vendors (most recently joined) - show all recent vendors, enhance with categories if available
    const latestVendors = [...vendors]
      .sort((a, b) => new Date(b.created_at || b.updated_at || now).getTime() - new Date(a.created_at || a.updated_at || now).getTime())
      .slice(0, 10) // Get 10 most recent vendors
      .map(vendor => {
        // Get unique categories for this vendor (if product data is available)
        const hasProducts = vendor.products && vendor.products.length > 0;
        
        const categories = hasProducts
          ? Array.from(new Set(vendor.products!.map(p => p.category).filter((cat): cat is string => Boolean(cat))))
          : [];
          
        console.log(`📊 Vendor ${vendor.name}: ${hasProducts ? `${vendor.products?.length} products` : 'no products'}, categories: ${categories.join(', ') || 'none'}`);
        
        return {
          id: vendor.id,
          name: vendor.name,
          email: vendor.email,
          joinDate: vendor.created_at || vendor.updated_at || now,
          status: vendor.status,
          categories: categories.length > 0 ? categories : ['General'],
          totalRevenue: vendor.totalRevenue || 0,
          totalOrders: vendor.totalOrders || 0
        };
      });

    // Top performers - show all top performers, enhance with categories if available
    const topPerformers = [...vendors]
      .sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0))
      .slice(0, 5)
      .map(vendor => {
        const hasProducts = vendor.products && vendor.products.length > 0;
        const categories = hasProducts
          ? Array.from(new Set(vendor.products!.map(p => p.category).filter((cat): cat is string => Boolean(cat))))
          : [];
        
        if (hasProducts) {
          console.log(`🏆 Top performer ${vendor.name}: ${vendor.products?.length || 0} products, categories:`, categories);
        }
        
        return {
          ...vendor,
          categories: categories.length > 0 ? categories : ['General']
        };
      });

    return {
      totalRevenue,
      totalOrders,
      avgOrderValue,
      newVendorsThisWeek,
      avgProductsPerVendor,
      vendorStatusCounts,
      vendorGrowthTrends,
      latestVendors,
      topPerformers,
    };
  }, [vendors]);

  // Filter and sort vendors
  const filteredAndSortedVendors = useMemo(() => {
    let filtered = vendors.filter(vendor => {
      const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          vendor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          vendor.phone.includes(searchTerm);
      
      const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Sort vendors
    filtered.sort((a, b) => {
      // Handle date fields
      if (sortField === 'created_at') {
        const aDate = new Date(a.created_at || a.updated_at || new Date()).getTime();
        const bDate = new Date(b.created_at || b.updated_at || new Date()).getTime();
        
        if (sortDirection === 'asc') {
          return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
        } else {
          return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
        }
      }
      
      // Handle string fields
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue as string).toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [vendors, searchTerm, statusFilter, sortField, sortDirection]);

  // Mutations for CRUD operations
  const createMutation = useMutation({
    mutationFn: (data: VendorFormData) => api.createVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors-all-with-products'] });
      showNotification('Vendor created successfully', 'success');
      handleClose();
    },
    onError: () => {
      showNotification('Failed to create vendor', 'error');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VendorFormData }) => 
      api.updateVendor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors-all-with-products'] });
      showNotification('Vendor updated successfully', 'success');
      handleClose();
    },
    onError: () => {
      showNotification('Failed to update vendor', 'error');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors-all-with-products'] });
      showNotification('Vendor deleted successfully', 'success');
    },
    onError: () => {
      showNotification('Failed to delete vendor', 'error');
    }
  });

  // Event Handlers
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleOpen = (vendor?: EnhancedVendor) => {
    if (vendor) {
      setSelectedVendor(vendor);
      setFormData({
        name: vendor.name,
        email: vendor.email,
        phone: vendor.phone,
        address: vendor.address,
        website: vendor.website || '',
        description: vendor.description || '',
        status: vendor.status,
      });
    } else {
      setSelectedVendor(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        website: '',
        description: '',
        status: 'active',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVendor(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
      if (selectedVendor) {
      updateMutation.mutate({ id: selectedVendor.id, data: formData });
      } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = async (vendorId: number) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      deleteMutation.mutate(vendorId);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selected.length} vendors?`)) {
      try {
        await Promise.all(selected.map(id => api.deleteVendor(id)));
        queryClient.invalidateQueries({ queryKey: ['vendors-all-with-products'] });
        showNotification(`${selected.length} vendors deleted successfully`, 'success');
        setSelected([]);
      } catch (error) {
        showNotification('Failed to delete some vendors', 'error');
      }
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(filteredAndSortedVendors.map(vendor => vendor.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (vendorId: number) => {
    setSelected(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Address', 'Joined Date', 'Status', 'Orders', 'Revenue'],
      ...filteredAndSortedVendors.map(vendor => [
        vendor.name,
        vendor.email,
        vendor.phone,
        vendor.address,
        new Date(vendor.created_at || vendor.updated_at || new Date()).toLocaleDateString(),
        vendor.status,
        vendor.totalOrders?.toString() || '0',
        vendor.totalRevenue?.toString() || '0'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendors.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    showNotification('Vendors exported successfully', 'success');
  };

  const handleToggleStatus = async (vendor: EnhancedVendor) => {
    const newStatus = vendor.status === 'active' ? 'inactive' : 'active';
    try {
      await api.updateVendor(vendor.id, {
        name: vendor.name,
        email: vendor.email,
        phone: vendor.phone,
        address: vendor.address,
        status: newStatus,
      });
      queryClient.invalidateQueries({ queryKey: ['vendors-all-with-products'] });
      showNotification(`Vendor ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
    } catch (error) {
      showNotification('Failed to update vendor status', 'error');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleViewVendorProfile = (vendor: EnhancedVendor) => {
    setSelectedVendorProfile(vendor);
    setCurrentTab(2); // Switch to profile tab
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header with Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <StoreIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{vendors.length}</Typography>
                  <Typography variant="body2" color="textSecondary">Total Vendors</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ActiveIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {vendors.filter(v => v.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Active Vendors</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InactiveIcon color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {vendors.filter(v => v.status === 'inactive').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Inactive Vendors</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Header with Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Vendors Management</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Vendor
        </Button>
        </Box>
      </Box>

      {/* Search and Filter Toolbar */}
      <Paper sx={{ mb: 2 }}>
        <Toolbar>
          <TextField
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, mr: 2 }}
          />
          
          <FormControl sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          {selected.length > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">
                {selected.length} selected
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleBulkDelete}
              >
                Delete Selected
              </Button>
            </Box>
          )}
        </Toolbar>
      </Paper>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            icon={<StoreIcon />} 
            label="Vendors List" 
            id="vendor-tab-0"
            aria-controls="vendor-tabpanel-0"
          />
          <Tab 
            icon={<AnalyticsIcon />} 
            label="Analytics Dashboard" 
            id="vendor-tab-1"
            aria-controls="vendor-tabpanel-1"
          />
          {selectedVendorProfile && (
            <Tab 
              icon={<PersonIcon />} 
              label={`${selectedVendorProfile.name} Profile`}
              id="vendor-tab-2"
              aria-controls="vendor-tabpanel-2"
            />
          )}

        </Tabs>
      </Paper>

      {/* Tab Panels */}
      
      {/* Tab 0: Vendors List */}
      <TabPanel value={currentTab} index={0}>
        <VendorsList 
          vendors={filteredAndSortedVendors}
          onViewProfile={handleViewVendorProfile}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          selected={selected}
          setSelected={setSelected}
          sortField={sortField}
          sortDirection={sortDirection}
          setSortField={setSortField}
          setSortDirection={setSortDirection}
          showNotification={showNotification}
        />
      </TabPanel>

      {/* Tab 1: Analytics Dashboard */}
      <TabPanel value={currentTab} index={1}>
        <AnalyticsDashboard 
          vendors={vendors}
          analyticsData={analyticsData}
          dashboardData={dashboardData}
        />
      </TabPanel>

      {/* Tab 2: Vendor Profile */}
      {selectedVendorProfile && (
        <TabPanel value={currentTab} index={2}>
          <VendorProfile 
            vendor={selectedVendorProfile}
            onBack={() => setCurrentTab(0)}
          />
        </TabPanel>
      )}



      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Vendors List Component
const VendorsList: React.FC<{
  vendors: EnhancedVendor[];
  onViewProfile: (vendor: EnhancedVendor) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: 'all' | 'active' | 'inactive';
  setStatusFilter: (filter: 'all' | 'active' | 'inactive') => void;
  selected: number[];
  setSelected: (selected: number[]) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  setSortField: (field: SortField) => void;
  setSortDirection: (direction: SortDirection) => void;
  showNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}> = ({ 
  vendors, 
  onViewProfile, 
  searchTerm, 
  setSearchTerm, 
  statusFilter, 
  setStatusFilter,
  selected,
  setSelected,
  sortField,
  sortDirection,
  setSortField,
  setSortDirection,
  showNotification
}) => {
  
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(vendors.map(vendor => vendor.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (vendorId: number) => {
    const newSelected = selected.includes(vendorId) 
      ? selected.filter((id: number) => id !== vendorId)
      : [...selected, vendorId];
    setSelected(newSelected);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <Box>
      {/* Vendors Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < vendors.length}
                  checked={vendors.length > 0 && selected.length === vendors.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Vendor
                </TableSortLabel>
              </TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'created_at'}
                  direction={sortField === 'created_at' ? sortDirection : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Joined Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendors.map((vendor) => (
              <TableRow 
                key={vendor.id} 
                selected={selected.includes(vendor.id)}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(vendor.id)}
                    onChange={() => handleSelect(vendor.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <StoreIcon color="action" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {vendor.name}
                      </Typography>
                      {vendor.website && (
                        <Typography variant="caption" color="textSecondary">
                          {vendor.website}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    {vendor.email && (
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{vendor.email}</Typography>
                      </Box>
                    )}
                    {vendor.phone && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">{vendor.phone}</Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(vendor.created_at || vendor.updated_at || new Date()).toLocaleDateString('en', { 
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(() => {
                      const joinDate = new Date(vendor.created_at || vendor.updated_at || new Date());
                      const now = new Date();
                      const diffTime = Math.abs(now.getTime() - joinDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      
                      if (diffDays === 1) return 'Today';
                      if (diffDays === 2) return 'Yesterday';
                      if (diffDays <= 7) return `${diffDays - 1} days ago`;
                      if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
                      if (diffDays <= 365) return `${Math.floor(diffDays / 30)} months ago`;
                      return `${Math.floor(diffDays / 365)} years ago`;
                    })()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={vendor.status}
                    color={vendor.status === 'active' ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="View Profile">
                  <IconButton
                    size="small"
                    color="primary"
                        onClick={() => onViewProfile(vendor)}
                  >
                        <PersonIcon />
                  </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Vendor">
                  <IconButton
                    size="small"
                        color="primary"
                  >
                        <EditIcon />
                  </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC<{
  vendors: EnhancedVendor[];
  analyticsData: any;
  dashboardData?: any;
}> = ({ vendors, analyticsData, dashboardData }) => {
  
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00'];
  
  // State for vendor growth filter
  const [growthFilter, setGrowthFilter] = useState<'day' | 'week' | 'month'>('week');

  // Calculate vendor growth data based on selected filter
  const getVendorGrowthData = () => {
    const now = new Date();
    let periods: { label: string; start: Date; end: Date }[] = [];
    
    if (growthFilter === 'day') {
      // Past 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);
        
        periods.push({
          label: date.toLocaleDateString('en', { month: 'short', day: 'numeric' }),
          start,
          end
        });
      }
    } else if (growthFilter === 'week') {
      // Past 6 weeks
      for (let i = 5; i >= 0; i--) {
        const end = new Date(now);
        end.setDate(end.getDate() - (i * 7));
        end.setHours(23, 59, 59, 999);
        
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        
        const startStr = start.toLocaleDateString('en', { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString('en', { month: 'short', day: 'numeric' });
        
        periods.push({
          label: `${startStr} - ${endStr}`,
          start,
          end
        });
      }
    } else {
      // Past 6 months
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now);
        date.setMonth(date.getMonth() - i);
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
        
        const startStr = start.toLocaleDateString('en', { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString('en', { month: 'short', day: 'numeric' });
        
        periods.push({
          label: `${startStr} - ${endStr}`,
          start,
          end
        });
      }
    }

    return periods.map(period => {
      // Count vendors created in this period
      const vendorsInPeriod = vendors.filter(vendor => {
        const createdDate = new Date(vendor.created_at || vendor.updated_at || now);
        return createdDate >= period.start && createdDate <= period.end;
      }).length;

      return {
        period: period.label,
        newVendors: vendorsInPeriod
      };
    });
  };

  const vendorGrowthData = getVendorGrowthData();

  return (
    <Box>
      {/* Overview Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    New Vendors This Week
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.newVendorsThisWeek}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Last 7 days
                  </Typography>
                </Box>
                <NewVendorIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Vendors
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.vendorStatusCounts.active || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    of {vendors.length} total
                  </Typography>
                </Box>
                <StoreIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Inactive Vendors
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.vendorStatusCounts.inactive || 0}
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    Need attention
                  </Typography>
                </Box>
                <PieChartIcon sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} mb={4}>
        {/* Vendor Growth Trends */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Vendor Growth Trends
                </Typography>
                <ButtonGroup size="small" variant="outlined">
                  <Button 
                    variant={growthFilter === 'day' ? 'contained' : 'outlined'}
                    onClick={() => setGrowthFilter('day')}
                  >
                    Daily
                  </Button>
                  <Button 
                    variant={growthFilter === 'week' ? 'contained' : 'outlined'}
                    onClick={() => setGrowthFilter('week')}
                  >
                    Weekly
                  </Button>
                  <Button 
                    variant={growthFilter === 'month' ? 'contained' : 'outlined'}
                    onClick={() => setGrowthFilter('month')}
                  >
                    Monthly
                  </Button>
                </ButtonGroup>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {growthFilter === 'day' && 'New vendors added each day (past 7 days)'}
                {growthFilter === 'week' && 'New vendors added each week (past 6 weeks)'}
                {growthFilter === 'month' && 'New vendors added each month (past 6 months)'}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vendorGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="period" 
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'New Vendors', angle: -90, position: 'insideLeft' }}
                  />
                  <RechartsTooltip 
                    formatter={(value: any) => [value, 'New Vendors Added']}
                    labelFormatter={(label) => `${growthFilter.charAt(0).toUpperCase() + growthFilter.slice(1)}: ${label}`}
                  />
                  <Bar 
                    dataKey="newVendors" 
                    fill="#82ca9d"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Latest Vendors Section */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Latest Vendors
                </Typography>
                <Chip 
                  label={`${analyticsData.latestVendors.length} Recent`}
                  color="primary"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Recently joined vendors and their product categories
              </Typography>
              
              <Grid container spacing={2}>
                {analyticsData.latestVendors.map((vendor: any, index: number) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={vendor.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                            <StoreIcon fontSize="small" />
                          </Avatar>
                          <Box flexGrow={1}>
                            <Typography variant="subtitle2" color="text.primary" fontWeight="medium" noWrap>
                              {vendor.name}
                            </Typography>
                            <Chip 
                              label={vendor.status}
                              color={vendor.status === 'active' ? 'success' : 'error'}
                              size="small"
                              sx={{ height: 16, fontSize: '0.65rem', mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                        
                        <Box mb={2}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Joined: {new Date(vendor.joinDate).toLocaleDateString()}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            Categories:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {vendor.categories.slice(0, 3).map((category: string, catIndex: number) => (
                              <Chip
                                key={catIndex}
                                label={category}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  '& .MuiChip-label': { px: 0.8 }
                                }}
                              />
                            ))}
                            {vendor.categories.length > 3 && (
                              <Chip
                                label={`+${vendor.categories.length - 3} more`}
                                size="small"
                                variant="outlined"
                                color="default"
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  '& .MuiChip-label': { px: 0.8 }
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {analyticsData.latestVendors.length === 0 && (
                <Box textAlign="center" py={4}>
                  <StoreIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No recent vendors
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New vendors will appear here when they join your platform
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>




    </Box>
  );
};

// Vendor Profile Component
const VendorProfile: React.FC<{
  vendor: EnhancedVendor;
  onBack: () => void;
}> = ({ vendor, onBack }) => {
  const [profileTab, setProfileTab] = useState(0);

  const handleProfileTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setProfileTab(newValue);
  };
  
  return (
    <Box>
      {/* Profile Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">{vendor.name}</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Vendor Profile & Management
          </Typography>
        </Box>
      </Box>

      {/* Profile Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={profileTab} 
          onChange={handleProfileTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<AnalyticsIcon />} label="Overview" />
          <Tab icon={<OrderIcon />} label={`Orders (${vendor.recentOrders?.length || 0})`} />
          <Tab icon={<StoreIcon />} label={`Products (${vendor.products?.length || 0})`} />
          <Tab icon={<BankIcon />} label="Bank Details" />
        </Tabs>
      </Paper>

      {/* Tab 0: Overview */}
      <TabPanel value={profileTab} index={0}>
        {/* Profile Overview */}
        <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ width: 60, height: 60, mr: 2 }}>
                  <StoreIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6">{vendor.name}</Typography>
                  <Chip 
                    label={vendor.status} 
                    color={vendor.status === 'active' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Contact Information
                </Typography>
                <Typography variant="body2">{vendor.email}</Typography>
                <Typography variant="body2">{vendor.phone}</Typography>
                <Typography variant="body2">{vendor.address}</Typography>
                {vendor.website && (
                  <Typography variant="body2" color="primary">
                    {vendor.website}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h5" color="primary">
                    {vendor.totalOrders}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Orders
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h5" color="success.main">
                    ${vendor.totalRevenue?.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Revenue
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h5" color="info.main">
                    {vendor.products?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Products
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h5" color="warning.main">
                    {vendor.totalRevenue && vendor.totalOrders 
                      ? Math.round(vendor.totalRevenue / vendor.totalOrders)
                      : 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Avg Order
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Revenue Chart */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Performance
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={vendor.monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" />
                  <Line type="monotone" dataKey="orders" stroke="#82ca9d" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Products, Orders & Payment Methods */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Products
              </Typography>
              <List>
                {vendor.products?.slice(0, 5).map((product) => (
                  <ListItem key={product.id}>
                    <ListItemText
                      primary={product.name}
                      secondary={`$${product.price} • ${product.sales} sold • $${product.revenue} revenue`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Orders
              </Typography>
              <List>
                {vendor.recentOrders?.slice(0, 5).map((order) => (
                  <ListItem key={order.id}>
                    <ListItemText
                      primary={`Order #${order.id} - ${order.customer}`}
                      secondary={`${order.date} • $${order.amount} • ${order.status}`}
                    />
                    <ListItemSecondaryAction>
                      <Chip 
                        label={order.status} 
                        size="small"
                        color={order.status === 'completed' ? 'success' : 'default'}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Payment Methods
                </Typography>
                <IconButton size="small" color="primary">
                  <AddIcon />
                </IconButton>
              </Box>
              <List>
                {vendor.paymentMethods?.map((method) => (
                                     <ListItem key={method.id} sx={{ px: 0 }}>
                     <ListItemAvatar>
                       <Avatar sx={{ width: 32, height: 32 }}>
                         <BankIcon fontSize="small" />
                       </Avatar>
                     </ListItemAvatar>
                     <ListItemText
                       primary={
                         <Box display="flex" alignItems="center" gap={1}>
                           <Typography variant="body2" fontWeight="medium">
                             {method.bankName}
                           </Typography>
                           {method.isDefault && (
                             <Chip 
                               label="Default"
                               color="primary"
                               size="small"
                               sx={{ height: 20, fontSize: '0.7rem' }}
                             />
                           )}
                         </Box>
                       }
                       secondary={
                         <Box>
                           <Typography variant="caption" display="block">
                             {method.accountName}
                           </Typography>
                           <Typography variant="caption" display="block">
                             {method.accountNumber} • {method.accountType}
                           </Typography>
                           {method.iban && (
                             <Typography variant="caption" color="textSecondary" display="block">
                               IBAN: {method.iban.substring(0, 8)}...
                             </Typography>
                           )}
                         </Box>
                       }
                     />
                     <ListItemSecondaryAction>
                       <Chip 
                         label={method.isActive ? 'Active' : 'Inactive'}
                         color={method.isActive ? 'success' : 'error'}
                         size="small"
                         variant="outlined"
                         sx={{ fontSize: '0.7rem' }}
                       />
                     </ListItemSecondaryAction>
                   </ListItem>
                ))}
              </List>
              
              {(!vendor.paymentMethods || vendor.paymentMethods.length === 0) && (
                <Box textAlign="center" py={2}>
                  <PaymentIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="textSecondary">
                    No payment methods configured
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                  >
                    Add Payment Method
            </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </TabPanel>

      {/* Tab 1: Orders */}
      <TabPanel value={profileTab} index={1}>
        <VendorOrders vendor={vendor} />
      </TabPanel>

      {/* Tab 2: Products */}
      <TabPanel value={profileTab} index={2}>
        <VendorProducts vendor={vendor} />
      </TabPanel>

      {/* Tab 3: Bank Details */}
      <TabPanel value={profileTab} index={3}>
        <VendorBankDetails vendor={vendor} />
      </TabPanel>
    </Box>
  );
};

// Vendor Orders Component
const VendorOrders: React.FC<{ vendor: EnhancedVendor }> = ({ vendor }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        All Orders for {vendor.name}
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendor.recentOrders?.map((order) => (
              <TableRow key={order.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" color="primary">
                    #{order.id}
                  </Typography>
                </TableCell>
                <TableCell>{order.customer}</TableCell>
                <TableCell>{new Date(order.date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="success.main">
                    ${order.amount.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>{order.items} items</TableCell>
                <TableCell>
                  <Chip
                    label={order.status}
                    color={
                      order.status === 'completed' ? 'success' :
                      order.status === 'pending' ? 'warning' : 'info'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="primary">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Vendor Products Component
const VendorProducts: React.FC<{ vendor: EnhancedVendor }> = ({ vendor }) => {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          All Products by {vendor.name}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
        >
          Add Product
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product Name</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Sales</TableCell>
              <TableCell>Revenue</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendor.products?.map((product) => (
              <TableRow key={product.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">
                    {product.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="primary">
                    ${product.price.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${product.stock} units`}
                    color={product.stock > 10 ? 'success' : product.stock > 5 ? 'warning' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{product.sales} sold</TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="success.main">
                    ${product.revenue.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                    color={product.stock > 0 ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <IconButton size="small" color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Vendor Bank Details Component
const VendorBankDetails: React.FC<{ vendor: EnhancedVendor }> = ({ vendor }) => {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Bank Account Details for {vendor.name}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
        >
          Add Bank Account
        </Button>
      </Box>

      <Grid container spacing={3}>
        {vendor.paymentMethods?.map((method) => (
          <Grid item xs={12} md={6} key={method.id}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar>
                      <BankIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{method.bankName}</Typography>
                      {method.isDefault && (
                        <Chip
                          label="Default Account"
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                  <Chip
                    label={method.isActive ? 'Active' : 'Inactive'}
                    color={method.isActive ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Account Holder Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {method.accountName}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Account Number
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {method.accountNumber}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Account Type
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                      {method.accountType}
                    </Typography>
                  </Grid>
                  
                  {method.iban && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        IBAN
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                        {method.iban}
                      </Typography>
                    </Grid>
                  )}
                  
                  {method.swiftCode && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        SWIFT/BIC Code
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {method.swiftCode}
                      </Typography>
                    </Grid>
                  )}
                  
                  {method.routingNumber && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Routing Number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {method.routingNumber}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Box display="flex" gap={1} mt={2}>
                  <Button size="small" startIcon={<EditIcon />}>
                    Edit
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />}>
                    Delete
                  </Button>
                  {!method.isDefault && (
                    <Button size="small" variant="outlined">
                      Set as Default
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {(!vendor.paymentMethods || vendor.paymentMethods.length === 0) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box textAlign="center" py={4}>
                  <BankIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No Bank Accounts Configured
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={3}>
                    Add a bank account to receive payments from your sales
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Add Your First Bank Account
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Vendors;